import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft } from "lucide-react";
import Barcode from "react-barcode";
import html2canvas from "html2canvas";
// @ts-expect-error: No types for bwip-js
import bwipjs from "bwip-js";
import dayjs from "dayjs";

import { Route as barcodeRoute } from "@/routes/barcode.index";

const DEFAULT_BAR_WIDTH = 200;
const DEFAULT_BAR_HEIGHT = 60;
const DEFAULT_MATRIX_WIDTH = 60;
const DEFAULT_MATRIX_HEIGHT = 60;

const GenerateBarcode: React.FC = () => {
  // Read itemsJson from search params
  const searchParams = barcodeRoute.useSearch();
  let stickerItems: any[] = [];
  if (searchParams && searchParams.itemsJson) {
    try {
      stickerItems = JSON.parse(searchParams.itemsJson);
    } catch (e) {
      stickerItems = [];
    }
  }

  const [barDimensions, setBarDimensions] = useState({ width: DEFAULT_BAR_WIDTH, height: DEFAULT_BAR_HEIGHT });
  const [matrixDimensions, setMatrixDimensions] = useState({ width: DEFAULT_MATRIX_WIDTH, height: DEFAULT_MATRIX_HEIGHT });
  const goBack = () => window.history.back();

  useEffect(() => {
    if (stickerItems.length > 0) {
      stickerItems.forEach((item, idx) => {
        const batch_number = item.batch_number || "";
        const expiry = item.expiry ? item.expiry : "";
        const barcodeValue = item.barcode ? item.barcode : batch_number;
        try {
          const canvas = document.createElement('canvas');
          bwipjs.toCanvas(canvas, {
            bcid: 'datamatrix',
            text: `\u001d01${barcodeValue}17${expiry ? dayjs(expiry).format("YYMMDD") : ""}10${batch_number}\u001d210000000000`,
            width: matrixDimensions.width, // <-- add this
            height: matrixDimensions.height, // <-- add this
          });
          const img = document.getElementById(`datamatrix-${idx}`) as HTMLImageElement | null;
          if (img && canvas) {
            img.src = canvas.toDataURL('image/png');
          }
        } catch (e) {
          console.log("Error: ", e)
        }
      });
    }
  }, [stickerItems, barDimensions, matrixDimensions]);

  return (
    <div className="w-full min-h-screen bg-background">
      <div className="flex items-center justify-between px-4 pt-4">
        <span className="flex items-center cursor-pointer" onClick={goBack}>
          <span className="grid place-items-center p-2 rounded-xl bg-[#ECEFF8]">
            <ArrowLeft size={16} />
          </span>
          <h1 className="text-2xl font-semibold ml-2">Download Stickers</h1>
        </span>
        <div className="flex gap-4">
          <div>
            <Label htmlFor="bar-width">Barcode Width</Label>
            <Input
              id="bar-width"
              className="my-2 w-20"
              type="number"
              min={50}
              max={600}
              value={barDimensions.width}
              onChange={e => setBarDimensions({ ...barDimensions, width: Number(e.target.value) })}
            />
          </div>
          <div>
            <Label htmlFor="bar-height">Barcode Height</Label>
            <Input
              id="bar-height"
              className="my-2 w-20"
              type="number"
              min={30}
              max={300}
              value={barDimensions.height}
              onChange={e => setBarDimensions({ ...barDimensions, height: Number(e.target.value) })}
            />
          </div>
          <div>
            <Label htmlFor="matrix-width">Matrix Width</Label>
            <Input
              id="matrix-width"
              className="my-2 w-20"
              type="number"
              min={20}
              max={200}
              value={matrixDimensions.width}
              onChange={e => setMatrixDimensions({ ...matrixDimensions, width: Number(e.target.value) })}
            />
          </div>
          <div>
            <Label htmlFor="matrix-height">Matrix Height</Label>
            <Input
              id="matrix-height"
              className="my-2 w-20"
              type="number"
              min={20}
              max={200}
              value={matrixDimensions.height}
              onChange={e => setMatrixDimensions({ ...matrixDimensions, height: Number(e.target.value) })}
            />
          </div>
        </div>
      </div>
      <Separator className="mt-4" />

      <div className="w-full p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {stickerItems.map((item, idx) => {
          const batch_number = item.batch_number || "";
          const expiry = item.expiry ? item.expiry : "";
          const barcodeValue = item.barcode ? item.barcode : batch_number;

          return (
            <div key={idx} className="border p-2 flex flex-col items-center justify-center rounded-lg shadow-sm w-full h-auto">
              <div className="w-full flex flex-col items-center justify-center">
                <div
                  id={`barcodeDiv-${idx}`}
                  className={`p-2 grid place-items-center w-full`}
                  onClick={() => {
                    const el = document.querySelector(`#barcodeDiv-${idx}`) as HTMLElement | null;
                    if (!el) return;
                    html2canvas(el).then(canvas => {
                      var link = document.createElement("a");
                      document.body.appendChild(link);
                      link.download = `${barcodeValue}.png`;
                      link.href = canvas.toDataURL("image/png");
                      link.target = "_blank";
                      link.click();
                      document.body.removeChild(link);
                    });
                  }}
                >
                  <Barcode value={barcodeValue} height={barDimensions.height} width={barDimensions.width / 100} />
                  <p style={{ fontSize: 12, marginTop: 0 }}>{item.trade_name} (AED {item.cost.toFixed(2)})</p>
                  <p style={{ fontSize: 9 }}>Batch: {batch_number}</p>
                  <p style={{ fontSize: 9 }}>Qty: {item.quantity} | VAT: {item.vat}%</p>
                </div>
                <p className="text-xs mt-2">Click barcode to save as image</p>
              </div>
              <div className="flex flex-col items-center justify-center mt-4 w-full">
                <div
                  id={`datamatrixDiv-${idx}`}
                  className={`p-2 h-[${matrixDimensions.height}px] w-[${matrixDimensions.width}px] grid place-items-center`}
                  onClick={() => {
                    const el = document.querySelector(`#datamatrixDiv-${idx}`) as HTMLElement | null;
                    if (!el) return;
                    html2canvas(el).then(canvas => {
                      var link = document.createElement("a");
                      document.body.appendChild(link);
                      link.download = `${barcodeValue}-${batch_number}.png`;
                      link.href = canvas.toDataURL("image/png");
                      link.target = "_blank";
                      link.click();
                      document.body.removeChild(link);
                    });
                  }}
                >
                  <img
                    id={`datamatrix-${idx}`}
                    width={matrixDimensions.width}
                    height={matrixDimensions.height}
                  />
                  <p style={{ textAlign: "center", fontSize: 12, marginTop: 2 }}>{item.trade_name} (AED {item.cost.toFixed(2)})</p>
                  <p style={{ textAlign: "center", fontSize: 9 }}>Batch: {batch_number}</p>
                  <p style={{ textAlign: "center", fontSize: 9 }}>Qty: {item.quantity} | VAT: {item.vat}%</p>
                </div>
                <p className="text-xs mt-2">Click datamatrix to save as image</p>
              </div>
            </div>
          );
        })}

      </div>
    </div>
  );
};

export default GenerateBarcode;